import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import FavouriteGroupSelectorModal from './FavouriteGroupSelectorModal';
import {
    FavouriteChangeDetail,
    FAVOURITE_EVENT_NAME,
    dispatchFavouriteChange,
    getFavouritesConfig,
    postFavouriteAction,
    toBool,
} from '../utils/favourites';

interface FavouriteButtonProps {
    productId: number;
    initialIsFavourite?: boolean | number | string;
    displayMode?: 'full' | 'compact' | 'icon' | string;
    className?: string;
    ajaxUrl?: string;
    nonce?: string;
    canToggle?: boolean;
    groupsEnabled?: boolean;
}

interface FavouriteActionResponse {
    product_id?: number;
    message?: string;
    is_favourite?: boolean | number | string;
    button_text?: string;
    icon_class?: string;
    group_name?: string;
    group_slug?: string;
}

const normalizeDisplayMode = (value: unknown): 'full' | 'compact' | 'icon' => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'compact' || normalized === 'icon') {
        return normalized;
    }

    return 'full';
};

const getToggleLabel = (isFavourite: boolean, mode: 'full' | 'compact' | 'icon'): string => {
    if (mode === 'compact') {
        return isFavourite ? 'Favourited' : 'Favourite';
    }

    return isFavourite ? 'Remove from Favourites' : 'Add to Favourites';
};

const FavouriteButton: React.FC<FavouriteButtonProps> = ({
    productId,
    initialIsFavourite = false,
    displayMode = 'full',
    className = '',
    ajaxUrl,
    nonce,
    canToggle,
    groupsEnabled,
}) => {
    const [isFavourite, setIsFavourite] = useState<boolean>(toBool(initialIsFavourite));
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);
    const mode = normalizeDisplayMode(displayMode);
    const runtimeConfig = getFavouritesConfig();
    const config = {
        ajax_url: ajaxUrl || runtimeConfig.ajax_url,
        nonce: nonce || runtimeConfig.nonce,
        can_toggle: typeof canToggle === 'boolean' ? canToggle : runtimeConfig.can_toggle,
        groups_enabled: typeof groupsEnabled === 'boolean' ? groupsEnabled : runtimeConfig.groups_enabled,
    };
    const canToggleFavourite = config.can_toggle === true
        || (config.can_toggle === undefined && !!config.nonce && !!config.ajax_url);
    const normalizedProductId = Number(productId || 0);

    useEffect(() => {
        setIsFavourite(toBool(initialIsFavourite));
    }, [initialIsFavourite, normalizedProductId]);

    useEffect(() => {
        const handleFavouriteChanged = (event: Event) => {
            const detail = (event as CustomEvent<FavouriteChangeDetail>).detail || {};
            const eventProductId = Number(detail.productId || detail.product_id || 0);
            if (eventProductId !== normalizedProductId) {
                return;
            }

            setIsFavourite(toBool(detail.isFavourite !== undefined ? detail.isFavourite : detail.is_favourite));
        };

        document.addEventListener(FAVOURITE_EVENT_NAME, handleFavouriteChanged);
        return () => {
            document.removeEventListener(FAVOURITE_EVENT_NAME, handleFavouriteChanged);
        };
    }, [normalizedProductId]);

    if (!canToggleFavourite || !Number.isFinite(normalizedProductId) || normalizedProductId <= 0) {
        return null;
    }

    const buttonLabel = getToggleLabel(isFavourite, mode);
    const buttonClasses = [
        'weka-react-favourite-btn',
        `weka-react-favourite-btn--${mode}`,
        isFavourite ? 'is-favourite' : '',
        isLoading ? 'is-loading' : '',
        className,
    ].filter(Boolean).join(' ');

    const handleSuccess = (detail: FavouriteChangeDetail) => {
        setIsFavourite(toBool(detail.isFavourite));
        if (detail.message) {
            toast.success(detail.message);
        }
    };

    const handleDirectToggle = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        try {
            const payload = await postFavouriteAction<FavouriteActionResponse>('toggle_favourite', {
                product_id: normalizedProductId,
            });

            const detail: FavouriteChangeDetail = {
                productId: Number(payload.product_id || normalizedProductId),
                isFavourite: toBool(payload.is_favourite),
                buttonText: String(payload.button_text || ''),
                iconClass: String(payload.icon_class || ''),
                message: String(payload.message || ''),
                groupName: typeof payload.group_name === 'string' ? payload.group_name : '',
                groupSlug: typeof payload.group_slug === 'string' ? payload.group_slug : '',
            };

            setIsFavourite(toBool(detail.isFavourite));
            dispatchFavouriteChange(detail);
            if (detail.message) {
                toast.success(detail.message);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update favourite.';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClick = () => {
        if (isLoading) {
            return;
        }

        if (config.groups_enabled === true) {
            setIsSelectorOpen(true);
            return;
        }

        void handleDirectToggle();
    };

    return (
        <>
            <button
                type="button"
                className={buttonClasses}
                onClick={handleClick}
                disabled={isLoading}
                aria-pressed={isFavourite}
                aria-label={buttonLabel}
            >
                <i
                    className={isLoading ? 'fas fa-spinner fa-spin' : (isFavourite ? 'fas fa-heart' : 'far fa-heart')}
                    aria-hidden="true"
                ></i>
                {mode !== 'icon' && <span>{buttonLabel}</span>}
            </button>
            {config.groups_enabled === true && (
                <FavouriteGroupSelectorModal
                    isOpen={isSelectorOpen}
                    onClose={() => setIsSelectorOpen(false)}
                    productId={normalizedProductId}
                    isFavourite={isFavourite}
                    onSuccess={handleSuccess}
                />
            )}
        </>
    );
};

export default FavouriteButton;
